import React from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaCheckCircle, FaTimes, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';

const Comparison: React.FC = () => {
  const casinos = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      rating: 4.8,
      bonus: '100% up to £100',
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48h',
      games: 500,
      license: 'UKGC',
      mobile: true,
      liveChat: true
    },
    {
      id: 'funcasino',
      name: 'Fun Casino',
      image: '/images/offers/funcasino.webp',
      rating: 4.7,
      bonus: '200% up to £200',
      minDeposit: '£20',
      wagering: '40x',
      payoutTime: '48-72h',
      games: 800,
      license: 'UKGC',
      mobile: true,
      liveChat: true
    },
    {
      id: 'kwiff',
      name: 'Kwiff',
      image: '/images/offers/kwiff.webp',
      rating: 4.9,
      bonus: '£30 Free Bet',
      minDeposit: '£10',
      wagering: 'N/A',
      payoutTime: '24h',
      games: 300,
      license: 'UKGC',
      mobile: true,
      liveChat: true
    }
  ];

  return (
    <>
      <SEO
        title="Compare UK Casino Offers - Side by Side Comparison"
        description="Compare the best UK casino offers side by side. Compare bonuses, features, payout times, and more to find your perfect match."
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          about: 'UK Casino Comparison'
        }}
      />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Compare Casino Offers
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Side-by-side comparison of the top UK casino offers. Find the perfect match for your gaming preferences.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Casino</th>
                    <th className="px-6 py-4 text-center font-semibold">Rating</th>
                    <th className="px-6 py-4 text-center font-semibold">Bonus</th>
                    <th className="px-6 py-4 text-center font-semibold">Min Deposit</th>
                    <th className="px-6 py-4 text-center font-semibold">Wagering</th>
                    <th className="px-6 py-4 text-center font-semibold">Payout</th>
                    <th className="px-6 py-4 text-center font-semibold">Games</th>
                    <th className="px-6 py-4 text-center font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {casinos.map((casino, idx) => (
                    <tr key={casino.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <img
                            src={casino.image}
                            alt={casino.name}
                            className="w-20 h-20 object-contain rounded-lg"
                            loading="lazy"
                          />
                          <div>
                            <div className="font-bold text-gray-900">{casino.name}</div>
                            <div className="text-sm text-gray-600">{casino.license} Licensed</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <FaStar className="text-yellow-400" />
                          <span className="font-semibold">{casino.rating}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="font-semibold text-primary-600">{casino.bonus}</div>
                      </td>
                      <td className="px-6 py-4 text-center text-gray-700">{casino.minDeposit}</td>
                      <td className="px-6 py-4 text-center text-gray-700">{casino.wagering}</td>
                      <td className="px-6 py-4 text-center text-gray-700">{casino.payoutTime}</td>
                      <td className="px-6 py-4 text-center text-gray-700">{casino.games}+</td>
                      <td className="px-6 py-4 text-center">
                        <Link
                          to={`/offers/${casino.id}`}
                          className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
                        >
                          View Review <FaArrowRight />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {casinos.map((casino) => (
              <div key={casino.id} className="bg-white p-6 rounded-xl shadow-lg">
                <img
                  src={casino.image}
                  alt={casino.name}
                  className="w-full h-32 object-contain rounded-lg mb-4"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{casino.name}</h3>
                <div className="flex items-center gap-1 mb-4">
                  <FaStar className="text-yellow-400" />
                  <span className="font-semibold">{casino.rating}/5</span>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    {casino.mobile ? (
                      <FaCheckCircle className="text-green-600" />
                    ) : (
                      <FaTimes className="text-red-600" />
                    )}
                    <span>Mobile App</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {casino.liveChat ? (
                      <FaCheckCircle className="text-green-600" />
                    ) : (
                      <FaTimes className="text-red-600" />
                    )}
                    <span>24/7 Support</span>
                  </div>
                </div>
                <Link
                  to={`/offers/${casino.id}`}
                  className="block w-full bg-primary-600 hover:bg-primary-700 text-white text-center py-3 rounded-lg font-semibold transition-colors"
                >
                  Read Full Review
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Comparison;


